import React, { useEffect } from "react";
import { View, StyleSheet, ScrollView, SafeAreaView } from "react-native";

import {
  Text,
  Input,
  Error,
  Button,
  Header,
  Loading,
  CheckBox,
  InputLabel,
  SafeLayout,
} from "components";
import { Controller, useForm } from "react-hook-form";

import css from "styles";

import { colors, sizes, strings } from "assets/constants";

import { useGetApkVersion, useUpdateApkVersion } from "clients/apis";
import toast from "components/toast";

const ApkVersion = () => {
  const { data, isLoading, error, isError, refetch, isRefetching } =
    useGetApkVersion();

  const leadInfo = data?.data?.data?.items?.filter(
    (e: any) => e?.type === "lead"
  )?.[0];

  const orderInfo = data?.data?.data?.items?.filter(
    (e: any) => e?.type === "wholesaler"
  )?.[0];

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    id: number;
    link: string;
    version: number;
    is_force: 0 | 1;
    order_id: number;
    order_link: string;
    order_version: number;
    order_is_force: 0 | 1;
  }>({
    defaultValues: {},
  });

  const updateApkVersionMut = useUpdateApkVersion();

  useEffect(() => {
    setValue("id", leadInfo?.id);
    setValue("version", leadInfo?.version);
    setValue("link", leadInfo?.links?.[0]?.link);
    setValue("is_force", leadInfo?.is_force);

    setValue("order_id", orderInfo?.id);
    setValue("order_version", orderInfo?.version);
    setValue("order_link", orderInfo?.links?.[0]?.link);
    setValue("order_is_force", orderInfo?.is_force);
  }, [data]);

  const _onUpdateLeadVersion = () => {
    const { id, is_force, version, link } = getValues();

    if (!link) {
      toast.warning({ message: "لینک دانلود برنامه را وارد کنید" });
      return;
    }

    const postData: UpdateApkVersionReqT = {
      id,
      is_force,
      links: [{ type: "DIRECT", link }],
      version,
    };

    updateApkVersionMut.mutate(postData);
  };

  const _onUpdateOrderVersion = () => {
    const {
      order_id: id,
      order_is_force: is_force,
      order_version: version,
      order_link: link,
    } = getValues();

    if (!link) {
      toast.warning({ message: "لینک دانلود برنامه را وارد کنید" });
      return;
    }

    const postData: UpdateApkVersionReqT = {
      id,
      is_force,
      links: [{ type: "DIRECT", link }],
      version,
    };

    updateApkVersionMut.mutate(postData);
  };

  const _renderContent = () => {
    if (isLoading) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <SafeAreaView style={{ flex: 1 }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ flexGrow: 1 }}
        >
          <View style={styles.section}>
            <Text color={colors.theme} weight="extraBold" size={"xxxl"}>
              نسخه سرنخ
            </Text>

            <InputLabel
              style={{ marginTop: 15 }}
              label={strings.app_version_code}
            />
            <Controller
              control={control}
              name="version"
              rules={{ required: { value: true, message: strings.fill_input } }}
              render={({ field: { value } }) => {
                const _onPlus = () => {
                  setValue("version", value + 1);
                };

                const _onMinus = () => {
                  if (Number(value) === Number(leadInfo?.version)) {
                    return;
                  }

                  setValue("version", value - 1);
                };

                return (
                  <View style={styles.counterContainer}>
                    <Button
                      onPress={_onPlus}
                      square={45}
                      bgColor={colors.transparent}
                      icon={{
                        name: "plus",
                        type: "materialCommunity",
                        color: colors.text.normal,
                      }}
                    />
                    <View style={styles.version}>
                      <Text size={"lg"} lang="en" weight="bold">
                        {value}
                      </Text>
                    </View>
                    <Button
                      onPress={_onMinus}
                      square={45}
                      bgColor={colors.transparent}
                      icon={{
                        name: "minus",
                        type: "materialCommunity",
                        color: colors.text.normal,
                      }}
                    />
                  </View>
                );
              }}
            />

            <InputLabel style={{ marginTop: 25 }} label={strings.app_link} />
            <Controller
              control={control}
              name="link"
              rules={{ required: { value: true, message: strings.fill_input } }}
              render={({ field: { value, onChange } }) => (
                <Input
                  value={value ?? ""}
                  onChangeText={onChange}
                  icon={{ type: "materialCommunity", name: "vector-link" }}
                  placeholder={strings.insert_app_link}
                  error={errors?.link?.message}
                />
              )}
            />

            <View style={{ marginTop: 20, marginBottom: 50 }}>
              <Controller
                control={control}
                name="is_force"
                render={({ field: { value, onChange } }) => {
                  return (
                    <CheckBox
                      value={value}
                      onChange={onChange}
                      title={strings.force_update}
                    />
                  );
                }}
              />
            </View>

            <View
              style={{ width: "100%", maxWidth: 400, alignSelf: "flex-start" }}
            >
              <Button
                loading={updateApkVersionMut?.isLoading}
                title={{ value: strings.confirm }}
                onPress={_onUpdateLeadVersion}
              />
            </View>
          </View>

          <View style={styles.section}>
            <Text color={colors.theme} weight="extraBold" size={"xxxl"}>
              نسخه ثبت سفارش
            </Text>
            <InputLabel
              style={{ marginTop: 15 }}
              label={strings.app_version_code}
            />
            <Controller
              control={control}
              name="order_version"
              rules={{ required: { value: true, message: strings.fill_input } }}
              render={({ field: { value } }) => {
                const _onPlus = () => {
                  setValue("order_version", value + 1);
                };

                const _onMinus = () => {
                  if (Number(value) === Number(orderInfo?.version)) {
                    return;
                  }

                  setValue("order_version", value - 1);
                };

                return (
                  <View style={styles.counterContainer}>
                    <Button
                      onPress={_onPlus}
                      square={45}
                      bgColor={colors.transparent}
                      icon={{
                        name: "plus",
                        type: "materialCommunity",
                        color: colors.text.normal,
                      }}
                    />
                    <View style={styles.version}>
                      <Text size={"lg"} lang="en" weight="bold">
                        {value}
                      </Text>
                    </View>
                    <Button
                      onPress={_onMinus}
                      square={45}
                      bgColor={colors.transparent}
                      icon={{
                        name: "minus",
                        type: "materialCommunity",
                        color: colors.text.normal,
                      }}
                    />
                  </View>
                );
              }}
            />

            <InputLabel style={{ marginTop: 25 }} label={strings.app_link} />
            <Controller
              control={control}
              name="order_link"
              rules={{ required: { value: true, message: strings.fill_input } }}
              render={({ field: { value, onChange } }) => (
                <Input
                  value={value ?? ""}
                  onChangeText={onChange}
                  icon={{ type: "materialCommunity", name: "vector-link" }}
                  placeholder={strings.insert_app_link}
                  error={errors?.order_link?.message}
                />
              )}
            />

            <View style={{ marginTop: 20, marginBottom: 50 }}>
              <Controller
                control={control}
                name="order_is_force"
                render={({ field: { value, onChange } }) => {
                  return (
                    <CheckBox
                      value={value}
                      onChange={onChange}
                      title={strings.force_update}
                    />
                  );
                }}
              />
            </View>

            <View
              style={{ width: "100%", maxWidth: 400, alignSelf: "flex-start" }}
            >
              <Button
                loading={updateApkVersionMut?.isLoading}
                title={{ value: strings.confirm }}
                onPress={_onUpdateOrderVersion}
              />
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  };

  return (
    <>
      <Header title={strings.app_version} />
      {_renderContent()}
    </>
  );
};

export default ApkVersion;

const styles = StyleSheet.create({
  counterContainer: {
    height: 48,
    alignItems: "center",
    flexDirection: "row-reverse",
    backgroundColor: colors.white,
    borderRadius: sizes.radius.lg,
    alignSelf: "flex-end",
    borderWidth: 1,
    borderColor: colors.black + "22",
  },

  version: {
    width: 70,
    alignItems: "center",
    justifyContent: "center",
  },

  section: {
    width: "90%",
    alignSelf: "center",
    backgroundColor: colors.white,
    padding: 22,
    marginVertical: 15,
    borderRadius: 15,
    ...css.shadow(10),
  },
});
